import {
  askGontijoTokenVerdict,
  type GontijoSessionVerdict,
} from '@/services/gontijo-session/gontijoAuthApi';

/**
 * How long a caller waits for a verdict before treating the backend as unanswered.
 *
 * The client sets no timeout of its own, so a request that never settles never answers. In the app
 * that meant the checkout's pay button sat disabled and spinning for the rest of the screen's life,
 * with no way for the buyer to pay and nothing on screen saying why. A deadline is the difference
 * between a slow backend and a stuck one.
 */
const SESSION_VERDICT_TIMEOUT_MS = 10_000;

/** No token is not a rejection: nothing was asked, so nothing was refused. */
const NO_SESSION: GontijoSessionVerdict = { valid: false, expired: false, unreachable: false };

interface InFlightVerdict {
  token: string;
  request: Promise<GontijoSessionVerdict>;
  startedAt: number;
}

/**
 * The verdict request in flight, so concurrent readers share one POST.
 *
 * Several things read the session at once — the gate, the payment button, the header — and a single
 * sign-in used to fire two `validate_token` calls in parallel because two of them reached here
 * together.
 */
let inFlight: InFlightVerdict | null = null;

/**
 * Whether a shared entry is still worth handing to a new caller.
 *
 * Only while it is *fresh*. Since the request can hang for the life of the page, handing a hung
 * promise to a caller arriving a minute later would be worse than the duplicate POST this avoids:
 * that caller would never get an answer, where before it would at least have asked.
 */
function isFresh(entry: InFlightVerdict, token: string, now: number): boolean {
  return entry.token === token && now - entry.startedAt < SESSION_VERDICT_TIMEOUT_MS;
}

/** Asks the backend about a token, sharing one request between concurrent readers. */
function readGontijoSessionVerdict(token: string | null): Promise<GontijoSessionVerdict> {
  if (!token) return Promise.resolve(NO_SESSION);

  if (inFlight && isFresh(inFlight, token, Date.now())) return inFlight.request;

  const request = askGontijoTokenVerdict(token);
  const entry: InFlightVerdict = { token, request, startedAt: Date.now() };
  inFlight = entry;

  // Only if this is still the read the entry points at: a newer token's may have replaced it.
  function release() {
    if (inFlight === entry) inFlight = null;
  }
  request.then(release, release);

  return request;
}

/**
 * The same verdict, with a deadline.
 *
 * Timing out stands for "there was a token and nobody would say whether it is good", which is what
 * `unreachable` already means everywhere else. Deliberately not `expired`: the buyer's session is
 * not what failed, and only `expired` justifies treating it as gone.
 */
async function readGontijoSessionVerdictWithDeadline(
  token: string | null,
  timeoutMs: number = SESSION_VERDICT_TIMEOUT_MS,
): Promise<GontijoSessionVerdict> {
  if (!token) return NO_SESSION;

  let timer: ReturnType<typeof setTimeout>;

  const deadline = new Promise<GontijoSessionVerdict>((resolve) => {
    timer = setTimeout(
      () => resolve({ valid: false, expired: false, unreachable: true }),
      timeoutMs,
    );
  });

  try {
    return await Promise.race([readGontijoSessionVerdict(token), deadline]);
  } finally {
    // Or the timer keeps a handle alive for its full length after the answer already landed.
    clearTimeout(timer);
  }
}

/**
 * True when the backend turned the session down, as opposed to never answering.
 *
 * Written here rather than negated by hand at each caller, because every gate in the funnel stops
 * on this and on nothing else.
 *
 * Only `expired` — the backend's 401, or its 400 for a malformed token — counts. Deliberately not
 * `!valid && !unreachable`, which also catches {@link NO_SESSION}: never having asked is not the
 * same as having been turned down, and a gate reading it that way would treat a buyer who was
 * simply never signed in as one the carrier rejected.
 */
function isGontijoSessionRejected({ expired }: GontijoSessionVerdict): boolean {
  return expired;
}

/**
 * Drops any shared request. Called whenever the session changes, which is what a sign-out is.
 *
 * Without this, a verdict about the previous buyer's token could still be handed to a reader that
 * arrives just after someone else signs in.
 */
function resetGontijoSessionVerdict(): void {
  inFlight = null;
}

export {
  NO_SESSION,
  SESSION_VERDICT_TIMEOUT_MS,
  isGontijoSessionRejected,
  readGontijoSessionVerdict,
  readGontijoSessionVerdictWithDeadline,
  resetGontijoSessionVerdict,
};
